/**
 * Changing an order that has already been taken.
 *
 * Customers move a pickup, add a cake for an aunt who is now coming, or drop
 * the one nobody wants — and they do it after paying about as often as before.
 * Until this existed the only answer was to cancel and re-ring, which loses the
 * order number the customer was given, loses its history, and re-books the
 * payment.
 *
 * Two things make this safe to open on a paid order. What has been paid is
 * never touched here, only what it is measured against: reducing an order that
 * was settled in full leaves a refund owing, and the screen says so in those
 * words before anything is saved. And the store room follows the change — the
 * server moves the difference, so adding a cake takes its ingredients out and
 * removing one puts them back.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MessageSquare, Percent, ShoppingBag, Trash2 } from 'lucide-react';
import {
  TIME_SLOTS,
  computeTotals,
  formatMoney,
  orderLineToCart,
  slotLabel,
  type CartLineInput,
  type Discount,
  type FulfillmentType,
  type OrderWithDetail,
} from '@bakery/shared';

import { api, type StockShortfallRow } from '../lib/api.js';
import { byName } from '../lib/catalog-order.js';
import { ItemNoteModal } from './ItemNote.js';
import { LineDiscountModal } from './LineDiscount.js';
import {
  Button,
  Field,
  Input,
  Modal,
  MoneyInput,
  NumberInput,
  Select,
  cx,
  useToast,
} from '../components/ui.js';

const FULFILLMENTS: { value: FulfillmentType; label: string }[] = [
  { value: 'pickup', label: 'Pick-up' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'dine_in', label: 'Dine in' },
  { value: 'courier', label: 'Courier' },
];

/** Split a stored timestamp into the two controls the shop actually sets. */
function splitDueAt(dueAt: string | null): { date: string; time: string } {
  if (!dueAt) return { date: '', time: '' };
  const at = new Date(dueAt);
  if (Number.isNaN(at.getTime())) return { date: '', time: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    // Snapped to the nearest half hour the picker offers, so an order booked
    // at 14:05 by some other route still shows a selected slot rather than a
    // blank one that silently rewrites the time on save.
    time: `${pad(at.getHours())}:${at.getMinutes() < 30 ? '00' : '30'}`,
  };
}

export function EditOrderModal({
  order,
  onClose,
  onSaved,
}: {
  order: OrderWithDetail;
  onClose: () => void;
  onSaved: (updated: OrderWithDetail) => void;
}) {
  const toast = useToast();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.catalog.products() });
  const discounts = useQuery({ queryKey: ['discounts'], queryFn: () => api.catalog.discounts() });

  /*
   * A–Z rather than the catalogue's own order, which exists to arrange the
   * till's grid and only makes a long dropdown harder to read. See
   * lib/catalog-order.
   */
  const pickable = useMemo(() => byName(products.data ?? []), [products.data]);

  const initialDue = splitDueAt(order.dueAt);
  const [dueDate, setDueDate] = useState(initialDue.date);
  const [dueTime, setDueTime] = useState(initialDue.time || TIME_SLOTS[0]!);
  const [fulfillment, setFulfillment] = useState<FulfillmentType>(order.fulfillment);
  const [deliveryFee, setDeliveryFee] = useState(order.deliveryFee);
  const [discountId, setDiscountId] = useState(order.discountId ?? '');
  const [lines, setLines] = useState<CartLineInput[]>(() => order.lines.map(orderLineToCart));
  const [discounting, setDiscounting] = useState<number | null>(null);
  const [shortfall, setShortfall] = useState<StockShortfallRow[] | null>(null);
  /** Saved, and now asking whether the customer should be told. */
  const [telling, setTelling] = useState(false);
  /** Which line's note is open for editing. */
  const [noting, setNoting] = useState<number | null>(null);

  const orderDiscounts = (discounts.data ?? []).filter((d: Discount) => d.scope !== 'item');
  const itemDiscounts = (discounts.data ?? []).filter((d: Discount) => d.scope === 'item');
  const selectedDiscount = orderDiscounts.find((d: Discount) => d.id === discountId) ?? null;

  /*
   * The same arithmetic the server will commit, run here so the new total and
   * the new balance are on screen before anybody presses save. Seeing "₱450
   * still to collect" beforehand is the entire point of the screen.
   */
  const totals = useMemo(
    () =>
      computeTotals({
        lines,
        discount: selectedDiscount,
        manualDiscount: order.manualDiscount,
        deliveryFee: fulfillment === 'delivery' ? deliveryFee : 0,
        amountPaid: order.amountPaid,
      }),
    [lines, selectedDiscount, order.manualDiscount, order.amountPaid, fulfillment, deliveryFee],
  );

  const refundDue = Math.max(order.amountPaid - totals.total, 0);

  const save = useMutation({
    mutationFn: () =>
      api.orders.update(order.id, {
        dueAt: dueDate ? new Date(`${dueDate}T${dueTime}`).toISOString() : null,
        fulfillment,
        deliveryFee: fulfillment === 'delivery' ? deliveryFee : 0,
        discountId: discountId || null,
        lines: lines.map((line) => ({
          productId: line.productId,
          variantId: line.variantId ?? null,
          name: line.name,
          qty: line.qty,
          unitPrice: line.unitPrice,
          modifiers: line.modifiers ?? [],
          discountAmount: line.discountAmount ?? 0,
          notes: line.notes ?? null,
        })),
      }),
    onSuccess: (result) => {
      /*
       * Reported, not refused, exactly as at the till. The customer has been
       * told they can have it; the shortfall is something to buy, not a reason
       * to refuse an order that is already on the books.
       */
      const short = (result.availability?.shortfalls ?? []) as StockShortfallRow[];
      if (short.length > 0) setShortfall(short);
      else {
        toast.push({ tone: 'ok', title: `${order.orderNo} updated` });
        /*
         * Offered, not sent. The shop's copy has changed and the customer is
         * still holding whatever they were told last — but plenty of edits are
         * a typo or an internal note, and a message for each of those trains
         * people to ignore them.
         */
        setTelling(true);
      }
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const nothingLeft = lines.length === 0;

  if (telling) {
    const saved = save.data?.order ?? order;
    return (
      <TellCustomer
        orderId={order.id}
        orderNo={order.orderNo}
        onDone={() => {
          setTelling(false);
          onSaved(saved);
        }}
      />
    );
  }

  if (shortfall) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Saved — but check the store room"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setShortfall(null);
              setTelling(true);
            }}
          >
            Got it
          </Button>
        }
      >
        <p className="mb-3 text-sm text-[var(--color-ink-soft)]">
          {order.orderNo} is saved. What was added to it uses more than the store room has:
        </p>
        <ul className="space-y-1 text-sm text-[var(--color-ink-soft)]">
          {shortfall.map((row) => (
            <li key={row.id}>
              <span className="font-medium text-[var(--color-ink)]">{row.name}</span> —{' '}
              {Math.round(row.onHandDisplay * 1000) / 1000} {row.stockUnit} left, needs{' '}
              {Math.round(row.requiredDisplay * 1000) / 1000} {row.stockUnit}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-[var(--color-ink-faint)]">
          Fix the count in Stock room, or buy more before it is baked.
        </p>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={`Edit ${order.orderNo}`}
        description="Changes the order itself. Payments already taken are not touched."
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={nothingLeft}
              onClick={() => save.mutate()}
            >
              Save changes
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Wanted on" hint="Leave the date empty for an order with no set time.">
              <div className="flex gap-2">
                {/* A floor on the date, so a narrow screen takes the
                    space out of the slack elsewhere rather than off the year. */}
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                  className="min-w-[8.5rem] flex-1"
                />
                <Select
                  value={dueTime}
                  onChange={(event) => setDueTime(event.target.value)}
                  disabled={!dueDate}
                  className="w-[6.75rem] shrink-0"
                >
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>
                      {slotLabel(slot)}
                    </option>
                  ))}
                </Select>
              </div>
            </Field>

            <Field label="How it is going out">
              <Select
                value={fulfillment}
                onChange={(event) => setFulfillment(event.target.value as FulfillmentType)}
              >
                {FULFILLMENTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            {fulfillment === 'delivery' && (
              <Field label="Delivery fee">
                <MoneyInput value={deliveryFee} onChange={setDeliveryFee} />
              </Field>
            )}
          </div>

          {/* Items ------------------------------------------------------ */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Items</h3>

            {lines.map((line, index) => (
              <div key={index} className="flex gap-2">
                <Select
                  value={line.productId ?? ''}
                  onChange={(event) => {
                    const product = products.data?.find((p) => p.id === event.target.value);
                    if (!product) return;
                    // Swapping the product re-prices the line at today's price;
                    // its own discount no longer describes anything and goes.
                    setLines((current) =>
                      current.map((l, i) =>
                        i === index
                          ? { ...l, productId: product.id, name: product.name, unitPrice: product.price, variantId: null, modifiers: [], discountAmount: 0 }
                          : l,
                      ),
                    );
                  }}
                  className="min-w-0 flex-1"
                >
                  {/* A line for something no longer in the catalogue still has
                      to be shown, or editing the date would silently swap it. */}
                  {!products.data?.some((p) => p.id === line.productId) && (
                    <option value={line.productId ?? ''}>{line.name}</option>
                  )}
                  {pickable.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} — {formatMoney(product.price)}
                    </option>
                  ))}
                </Select>
                <NumberInput
                  inputMode="numeric"
                  value={line.qty}
                  onChange={(qty) =>
                    setLines((current) =>
                      current.map((l, i) => (i === index ? { ...l, qty: Math.max(1, qty || 1) } : l)),
                    )
                  }
                  className="tabular w-16 shrink-0 text-center"
                  aria-label={`Quantity of ${line.name}`}
                />
                {itemDiscounts.length > 0 && (
                  <Button
                    variant="ghost"
                    aria-label={`Discount ${line.name}`}
                    title="Discount this item"
                    className={cx('shrink-0 px-2.5', (line.discountAmount ?? 0) > 0 && 'text-[var(--color-ok)]')}
                    onClick={() => setDiscounting(index)}
                    icon={<Percent className="size-4" />}
                  />
                )}
                <Button
                  variant="ghost"
                  aria-label={`Note on ${line.name}`}
                  title="Add a note to this item"
                  className={cx('shrink-0 px-2.5', line.notes && 'text-crust-600')}
                  onClick={() => setNoting(index)}
                  icon={<MessageSquare className="size-4" />}
                />
                <Button
                  variant="ghost"
                  aria-label={`Remove ${line.name}`}
                  title="Remove this item"
                  className="shrink-0 px-2.5 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                  onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                  icon={<Trash2 className="size-4" />}
                />
              </div>
            ))}

            {nothingLeft && (
              <p className="text-sm text-[var(--color-warn)]">
                An order needs at least one item. Put something back, or close this and cancel the order
                instead.
              </p>
            )}

            <Button
              size="sm"
              icon={<ShoppingBag className="size-4" />}
              disabled={!products.data?.length}
              onClick={() =>
                setLines((current) => [
                  ...current,
                  {
                    productId: products.data![0]!.id,
                    name: products.data![0]!.name,
                    qty: 1,
                    unitPrice: products.data![0]!.price,
                    modifiers: [],
                    discountAmount: 0,
                  },
                ])
              }
            >
              Add an item
            </Button>
          </section>

          {orderDiscounts.length > 0 && (
            <Field label="Discount on the whole order">
              <Select value={discountId} onChange={(event) => setDiscountId(event.target.value)}>
                <option value="">No discount on the whole order</option>
                {orderDiscounts.map((discount: Discount) => (
                  <option key={discount.id} value={discount.id}>
                    {discount.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {/* What it comes to ------------------------------------------- */}
          <section className="space-y-1 rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] px-4 py-3 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--color-ink-soft)]">Subtotal</span>
              <span className="tabular">{formatMoney(totals.subtotal)}</span>
            </div>
            {totals.discountAmount > 0 && (
              <div className="flex justify-between text-[var(--color-ok)]">
                <span>Discount</span>
                <span className="tabular">−{formatMoney(totals.discountAmount)}</span>
              </div>
            )}
            {totals.deliveryFee > 0 && (
              <div className="flex justify-between">
                <span className="text-[var(--color-ink-soft)]">Delivery</span>
                <span className="tabular">{formatMoney(totals.deliveryFee)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-[var(--color-line)] pt-1.5 font-semibold">
              <span>Total</span>
              <span className="tabular">
                {formatMoney(totals.total)}
                {totals.total !== order.total && (
                  <span className="ml-2 text-xs font-normal text-[var(--color-ink-faint)]">
                    was {formatMoney(order.total)}
                  </span>
                )}
              </span>
            </div>
            {order.amountPaid > 0 && (
              <div className="flex justify-between text-[var(--color-ink-soft)]">
                <span>Already paid</span>
                <span className="tabular">−{formatMoney(order.amountPaid)}</span>
              </div>
            )}

            {/*
              * The one thing that must not be discovered after saving: an order
              * that was settled in full and has just been made smaller owes the
              * customer money back.
              */}
            {refundDue > 0 ? (
              <div className="flex justify-between font-semibold text-[var(--color-warn)]">
                <span>Refund the customer</span>
                <span className="tabular">{formatMoney(refundDue)}</span>
              </div>
            ) : totals.balanceDue > 0 ? (
              <div className="flex justify-between font-semibold text-[var(--color-warn)]">
                <span>Still to collect</span>
                <span className="tabular">{formatMoney(totals.balanceDue)}</span>
              </div>
            ) : (
              <div className="flex justify-between font-semibold text-[var(--color-ok)]">
                <span>Settled in full</span>
                <span className="tabular">{formatMoney(0)}</span>
              </div>
            )}
          </section>
        </div>
      </Modal>

      {noting !== null && lines[noting] && (
        <ItemNoteModal
          line={lines[noting]!}
          onClose={() => setNoting(null)}
          onSave={(notes) => {
            setLines((current) =>
              current.map((l, i) => (i === noting ? { ...l, notes: notes.trim() || null } : l)),
            );
            setNoting(null);
          }}
        />
      )}

      {discounting !== null && totals.lines[discounting] && (
        <LineDiscountModal
          line={totals.lines[discounting]!}
          discounts={itemDiscounts}
          onClose={() => setDiscounting(null)}
          onApply={(amount) => {
            setLines((current) => current.map((l, i) => (i === discounting ? { ...l, discountAmount: amount } : l)));
            setDiscounting(null);
          }}
        />
      )}
    </>
  );
}

/**
 * After an edit: show the customer what they would now be told, and send it
 * only if somebody says so.
 *
 * Editing an order changes the shop's copy and nothing else. The customer is
 * still holding the message from before the change — the old time, the old
 * items, the old total — and there is no status change to hang a fresh one on,
 * because the order is in the same status it was.
 *
 * The text is read before it goes out. This is the shop contradicting
 * something the customer has already been told, so "what exactly will they
 * receive" is the question worth answering first.
 */
function TellCustomer({
  orderId,
  orderNo,
  onDone,
}: {
  orderId: string;
  orderNo: string;
  onDone: () => void;
}) {
  const toast = useToast();
  const [body, setBody] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['order-notify-preview', orderId],
    queryFn: () => api.orders.notifyPreview(orderId),
  });

  const send = useMutation({
    mutationFn: () => api.orders.notify(orderId, body !== null ? { bodyOverride: body } : {}),
    onSuccess: ({ notification }) => {
      if (notification.status === 'sent') {
        toast.push({ tone: 'ok', title: `${orderNo} — customer told`, body: `Sent on ${notification.channel}.` });
      } else {
        /*
         * Queued means the platform refused but there is an app to finish it
         * in; failed means nothing went. Neither is a success, and neither is
         * worth losing — the edit is saved either way.
         */
        toast.push({
          tone: 'warn',
          title: notification.status === 'queued' ? 'Open the app to finish sending' : 'Not sent',
          body: notification.error ?? 'The channel would not take it.',
        });
      }
      onDone();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Not sent', body: error.message }),
  });

  const blocked = preview.data?.blocked ?? null;
  const text = body ?? preview.data?.body ?? '';

  return (
    <Modal
      open
      onClose={onDone}
      size="lg"
      title={`${orderNo} saved`}
      description="The customer still has the details from before this change."
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Don't send
          </Button>
          <Button
            variant="primary"
            loading={send.isPending}
            disabled={preview.isLoading || Boolean(blocked) || !text.trim()}
            onClick={() => send.mutate()}
          >
            Send the update
          </Button>
        </>
      }
    >
      {preview.isLoading ? (
        <p className="text-sm text-[var(--color-ink-soft)]">Working out what to say…</p>
      ) : blocked ? (
        <p className="rounded-[var(--radius-md)] border border-[var(--color-warn)]/40 bg-[var(--color-warn-soft)] px-3 py-2.5 text-sm">
          {blocked}. The order is saved either way — tell them yourself if it matters.
        </p>
      ) : (
        <div className="space-y-3">
          <Field
            label="Message"
            hint={
              preview.data?.channel
                ? `Goes out on ${preview.data.channel}. Edit it first if you want to.`
                : 'Edit it first if you want to.'
            }
          >
            <textarea
              rows={10}
              value={text}
              onChange={(event) => setBody(event.target.value)}
              className="min-h-[8rem] w-full resize-y rounded-[var(--radius-md)] border border-[var(--color-line)] bg-transparent px-3 py-2.5 text-sm leading-relaxed focus:border-crust-500 focus:outline-none"
            />
          </Field>
          <p className="text-xs text-[var(--color-ink-faint)]">
            Built from the “{preview.data?.statusName}” template with the order as it now stands.
          </p>
        </div>
      )}
    </Modal>
  );
}
